import React, { useEffect } from "react";
import { ConfirmDialog, ensure, ValidationFailure, ValidationResult } from "sprint-common-web";

import { useFileRecordApi } from "../../../../../common/api/master-api/file-records/useFileRecordApi";
import { RecordType } from "../../../../../common/types/RecordType";
import { AssetOperationSection } from "../../../../common/asset-operations/AssetOperationSection";
import { CloudAssetOperationList } from "../../../../common/asset-operations/CloudAssetOperationList";
import { OnPremAssetOperationList } from "../../../../common/asset-operations/OnPremAssetOperationList";
import { useFileRecordDetails } from "../../../details/common/useFileRecordDetails";
import { CloudFileQcDoneValidationPanel, FILE_RECORD_VALIDATION_PREFIX } from "./CloudFileQcDoneValidationPanel";
import { useCloudFileQcDone } from "./useCloudFileQcDone";

export interface CloudFileQcDoneDialogProps {
    cloudFileId: string;
    cloudFileRecordType: RecordType;
    fileRecordId?: string;
    isOpen: boolean;
    width?: number;
    validate: ({ cloudFileId }: { cloudFileId: string }) => Promise<ValidationResult>;
    onClose: () => void;
    onHandleConfirmation?: () => void;
}

export const CloudFileQcDoneDialog = ({
    validate,
    isOpen,
    width,
    onClose,
    onHandleConfirmation,
    cloudFileId,
    cloudFileRecordType,
    fileRecordId
}: CloudFileQcDoneDialogProps): JSX.Element => {
    const [isConfirmed, setIsConfirmed] = React.useState(false);
    const [isValidating, setIsValidating] = React.useState(false);
    const [isOverriding, setIsOverriding] = React.useState(false);
    const [validationResult, setValidationResult] = React.useState<ValidationResult | undefined>(undefined);

    const { data: fileRecordDetails } = useFileRecordDetails(fileRecordId);

    const { cloudFileQcDone } = useCloudFileQcDone({ invalidationKey: cloudFileId });

    const { overrideFileRecordWithCloudFile } = useFileRecordApi();

    useEffect(() => {
        if (isOpen) {
            handleValidation();
        }
    }, [isOpen]);

    const handleConfirmation = async () => {
        await cloudFileQcDone.mutateAsync({ cloudFileId });

        setIsConfirmed(true);

        if (onHandleConfirmation) {
            onHandleConfirmation();
        }
    };

    const handleValidation = () => {
        setIsValidating(true);

        validate({ cloudFileId })
            .then((result) => setValidationResult(result))
            .finally(() => {
                setIsValidating(false);
            });
    };

    const handleOverrideFileRecordWithCloudFile = async () => {
        ensure(fileRecordId, "File record id is missing");

        try {
            setIsOverriding(true);

            await overrideFileRecordWithCloudFile({
                cloudFileId,
                fileRecordId
            });

            handleValidation();
        } finally {
            setIsOverriding(false);
        }
    };

    const confirmText = isConfirmed
        ? "The cloud file has been changed to Qc Done."
        : "Are you sure you want to update the given cloud file to Qc Done?";

    const { cloudFileValidationFailures, fileRecordValidationFailures } = getSortedFailures(validationResult);

    const isCloudFileValidationSuccessful = cloudFileValidationFailures.length === 0;
    const isFileRecordValidationSuccessful = fileRecordValidationFailures.length === 0;

    return (
        <ConfirmDialog
            width={width}
            title="Change status to Qc Done"
            text={confirmText}
            hidden={!isOpen}
            isBlocking={isConfirmed}
            confirm={{
                onClick: handleConfirmation,
                disabled: !isCloudFileValidationSuccessful || isValidating || isConfirmed || isOverriding
            }}
            cancel={{ onClick: onClose, buttonText: isConfirmed ? "Close" : "Cancel" }}
        >
            {!isConfirmed && (
                <CloudFileQcDoneValidationPanel
                    cloudFileRecordType={cloudFileRecordType}
                    fileRecordType={fileRecordDetails?.type}
                    fileRecordStatus={fileRecordDetails?.status}
                    cloudFileValidationFailures={cloudFileValidationFailures}
                    fileRecordValidationFailures={fileRecordValidationFailures}
                    isValidating={isValidating}
                    onFileRecordOverride={handleOverrideFileRecordWithCloudFile}
                />
            )}
            {validationResult && isCloudFileValidationSuccessful && (
                <AssetOperationSection isQcDoneConfirmed={isConfirmed}>
                    <CloudAssetOperationList cloudFileId={cloudFileId} />
                    {isFileRecordValidationSuccessful && <OnPremAssetOperationList fileRecordId={fileRecordId} />}
                </AssetOperationSection>
            )}
        </ConfirmDialog>
    );
};

const getSortedFailures = (validationResult?: ValidationResult) => {
    const failures = validationResult?.failures ?? [];

    const cloudFailures: ValidationFailure[] = [];
    const fileFailures: ValidationFailure[] = [];

    for (const failure of failures) {
        if (isFileRecordFailure(failure)) {
            fileFailures.push(failure);
        } else {
            cloudFailures.push(failure);
        }
    }

    return {
        cloudFileValidationFailures: cloudFailures,
        fileRecordValidationFailures: fileFailures
    };
};

const isFileRecordFailure = (failure: ValidationFailure): boolean =>
    failure.code.startsWith(FILE_RECORD_VALIDATION_PREFIX);
